/** Small formatting helpers shared by the Study plan components. */
import type { TFunction } from 'i18next';
import type { CoachLocale, PlanAccess, TodayPlan } from './types';

/** 'YYYY-MM-DD' (a calendar day, no time zone) as a date the browser can format without shifting it. */
const calendarDay = (date: string): Date => new Date(`${date}T12:00:00Z`);

export function formatDay(date: string, language: string, options: Intl.DateTimeFormatOptions): string {
  return new Intl.DateTimeFormat(language, { ...options, timeZone: 'UTC' }).format(calendarDay(date));
}

/** An instant such as the exam date, shown as the Tashkent calendar day it falls on. */
export function formatInstantAsTashkentDay(iso: string, language: string, options: Intl.DateTimeFormatOptions): string {
  return new Intl.DateTimeFormat(language, { ...options, timeZone: 'Asia/Tashkent' }).format(new Date(iso));
}

/** The i18n key for a subject; callers fall back to the raw key when it has no translation. */
export const subjectKey = (subject: string) => `studyPlan.subject_${subject}`;

/**
 * "15 000", grouped with a non-breaking space like `formatGroupedNumber` in lib/dateFormat. Not imported from there,
 * because that module starts the app's i18n, and these helpers are meant to stay light.
 */
const groupedSom = (value: number): string => String(Math.round(Math.max(0, value))).replace(/\B(?=(\d{3})+(?!\d))/g, '\u00a0');

/**
 * What a suggested test costs, said on every suggestion so none leads to a paywall unannounced: free, with a
 * subscription (the start page says whether the learner's covers it), or its price. A test from an older server,
 * which only ever suggested free tests, is free.
 */
export function testCost(test: { accessType?: PlanAccess; priceSom?: number }, t: TFunction): string {
  if (test.accessType === 'PAID') return `${groupedSom(test.priceSom ?? 0)} ${t('portal_tests_currency')}`;
  if (test.accessType === 'PLAN') return t('studyPlan.cost_plan');
  return t('free');
}

/**
 * Where a "see all" link goes and what it says: the learner's one subject within their exam when the plan has
 * one, so a mathematics learner is not sent through every subject of it; else the exam; else the whole catalog.
 */
export function catalogLink(goal: TodayPlan['goal'], t: TFunction): { to: string; label: string } {
  const { catalogCategory: category, catalogSubject: subject } = goal;
  if (category && subject) {
    const query = new URLSearchParams({ category, search: subject.search });
    return { to: `/portal/tests?${query}`, label: t('studyPlan.see_subject', { subject: t(subjectKey(subject.key), { defaultValue: subject.key }) }) };
  }
  if (category) {
    const exam = t(`studyPlan.exam_${goal.targetExam}`, { defaultValue: goal.targetExam });
    return { to: `/portal/tests?category=${encodeURIComponent(category)}`, label: t('studyPlan.see_all', { exam }) };
  }
  return { to: '/portal/tests', label: t('studyPlan.browse_all') };
}

/** The i18n key for an exam's name. */
export const examKey = (exam: string) => `studyPlan.exam_${exam}`;

/** The i18n key for a goal's grade, IELTS/SAT level or attestation category. */
export const levelKey = (level: string) => `studyPlan.grade_${level}`;

/** Tashkent's calendar day for `now`, as 'YYYY-MM-DD' (UTC+5, no daylight saving). */
export function tashkentToday(now: Date = new Date()): string {
  return new Date(now.getTime() + 5 * 3_600_000).toISOString().slice(0, 10);
}

/** The Tashkent calendar day an instant falls on, as 'YYYY-MM-DD'. */
export const tashkentDay = (iso: string): string => tashkentToday(new Date(iso));

/** 'YYYY-MM-DD' plus a number of days. */
export function addDays(date: string, days: number): string {
  return new Date(calendarDay(date).getTime() + days * 86_400_000).toISOString().slice(0, 10);
}

/** The exam-date input's value as the instant the server expects: midnight that day in Tashkent. */
export function tashkentMidnightIso(date: string): string {
  return new Date(`${date}T00:00:00+05:00`).toISOString();
}

const MESSAGE_LANGUAGES: readonly CoachLocale[] = ['uz', 'ru', 'en'];

/** The app's language as one the coach and the reminders write in; anything else falls back to English. */
export function messageLanguage(language: string | undefined): CoachLocale {
  const base = (language ?? '').slice(0, 2).toLowerCase();
  return MESSAGE_LANGUAGES.find((locale) => locale === base) ?? 'en';
}
